import React from "react";

import { BreadCrumbComponent } from "../ui/BreadCrumbComponent";
import {
  Column,
  DataTable,
  PageHeader,
  Pagination,
  SearchBar,
} from "../ui";
import type { Action } from "../ui";

type PaginationState = { page: number; pageSize: number };

type Props<T> = {
  /** Page header */
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  /** Primary page actions (e.g. "Add Climate"). */
  actions?: Action[];

  /** Optional toolbar rendered between the header and the table — e.g. bulk-select bar. */
  toolbar?: React.ReactNode;

  /** Data + shape. */
  rows: T[] | null | undefined;
  columns: Column<T>[];
  rowKey: (row: T) => string | number;
  rowActions?: (row: T) => Action[];
  loading?: boolean;
  empty?: React.ReactNode;

  /** Search. Pass undefined to omit the search bar. */
  search?: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    hint?: React.ReactNode;
  };

  /** Pagination. Omit to hide. */
  pagination?: {
    page: number;
    pageSize: number;
    total: number | null | undefined;
    onChange: (next: PaginationState) => void;
    pageSizeOptions?: number[];
  };

  /** Slot at the top — breadcrumb or extra banner. Defaults to the app breadcrumb. */
  topSlot?: React.ReactNode;

  /** Extra content rendered below the table (charts, footnotes, etc.). */
  footer?: React.ReactNode;
};

/**
 * Single page shell for list-style CRUD screens. Every Overview.jsx that
 * fits the "search + table + edit/delete per row + pagination" archetype
 * should render via this shell so the visual language stays identical.
 *
 * All existing per-page action handlers are preserved — they flow through
 * the `actions` / `rowActions` / `toolbar` slots unchanged.
 */
function ListPage<T>({
  title,
  subtitle,
  icon,
  actions,
  toolbar,
  rows,
  columns,
  rowKey,
  rowActions,
  loading,
  empty,
  search,
  pagination,
  topSlot,
  footer,
}: Props<T>) {
  const count =
    pagination?.total !== undefined && pagination?.total !== null
      ? pagination.total
      : Array.isArray(rows)
      ? rows.length
      : undefined;

  return (
    <div className="relative h-full w-full overflow-y-auto overflow-x-hidden bg-gray-50">
      <div className="mx-auto w-full max-w-6xl space-y-5 px-4 py-5 sm:px-6">
        {topSlot ?? <BreadCrumbComponent />}

        <PageHeader
          title={title}
          subtitle={subtitle}
          icon={icon}
          count={count}
          actions={actions}
        />

        {search ? (
          <SearchBar
            value={search.value}
            onChange={search.onChange}
            placeholder={search.placeholder}
            hint={search.hint}
            className="max-w-md"
          />
        ) : null}

        {toolbar}

        <DataTable
          columns={columns}
          rows={rows}
          rowKey={rowKey}
          rowActions={rowActions}
          loading={loading}
          empty={empty}
        />

        {pagination ? (
          <Pagination
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            pageSizeOptions={pagination.pageSizeOptions}
            onChange={pagination.onChange}
          />
        ) : null}

        {footer}
      </div>
    </div>
  );
}

export default ListPage;
